import { persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { combineReducers } from 'redux';
import { configureStore, createListenerMiddleware } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import themeSlice from '@/redux/theme/themeSlice';
import languageSlice from '@/redux/language/languageSlice';
import authSlice, { clearAuth } from '@/redux/auth/authSlice';
import { baseApi } from '@/api/baseApi';
import securitySlice from '@/redux/security/securitySlice';
import otpSlice from '@/redux/otp/otpSlice';
import notificationsSlice from '@/redux/notifications/notificationsSlice';

const rootPersistConfig = {
  key: 'midani_' + import.meta.env.VITE_ENVIRONMENT,
  storage: storage,
  // The RTK Query cache is transient server state — never persist it. The
  // unread-notifications count is transient too: it is rewritten from the
  // `custom-notifications` header on the first authenticated response, and a
  // stale persisted number would only flash on reload.
  blacklist: [baseApi.reducerPath, notificationsSlice.name],
};

const reducers = combineReducers({
  language: languageSlice.reducer,
  theme: themeSlice.reducer,
  auth: authSlice.reducer,
  security: securitySlice.reducer,
  otp: otpSlice.reducer,
  notifications: notificationsSlice.reducer,
  [baseApi.reducerPath]: baseApi.reducer,
});

const persistedReducer = persistReducer(rootPersistConfig, reducers);

// Ending a session must also drop the RTK Query cache. It is in-memory only
// (blacklisted above), so without this a logout → login inside the same page
// session re-serves the previous user's cached data (e.g. the company list)
// until a full reload. Listening on clearAuth covers every exit path: the
// logout menu item and the 401-refresh-failed branch in axiosBaseQuery.
const sessionListener = createListenerMiddleware();
sessionListener.startListening({
  actionCreator: clearAuth,
  effect: (_action, listenerApi) => {
    listenerApi.dispatch(baseApi.util.resetApiState());
  },
});

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .prepend(sessionListener.middleware)
      .concat(baseApi.middleware),
});

// Enables refetchOnFocus / refetchOnReconnect behavior for RTK Query.
setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;
